import Vue from 'vue';
import { normalizeId } from '@/helpers/IdHelper';
const STALE_SNAPSHOT_WINDOW_MS = 10000;
const stampKey = (raw) => {
    if (raw == null)
        return null;
    const value = String(raw);
    return value === '' ? null : normalizeId(value);
};
export const defineSyncSchema = (schema) => {
    schema.overlays?.forEach((overlay) => {
        Object.freeze(overlay.fields);
        Object.freeze(overlay);
    });
    if (schema.overlays)
        Object.freeze(schema.overlays);
    if (schema.children) {
        Object.values(schema.children).forEach(defineSyncSchema);
        Object.freeze(schema.children);
    }
    return Object.freeze(schema);
};
export const schemaKinds = (schema) => {
    const kinds = new Set();
    if (schema.kind !== null)
        kinds.add(schema.kind);
    schema.overlays?.forEach((overlay) => {
        kinds.add(overlay.kind);
    });
    Object.values(schema.children ?? {}).forEach((child) => {
        schemaKinds(child).forEach((kind) => kinds.add(kind));
    });
    return [...kinds];
};
export const createSyncState = (kinds) => {
    const stamps = {};
    kinds.forEach((kind) => {
        stamps[kind] = {};
    });
    return { seq: 0, appliedSeq: 0, stamps };
};
export const recordWrite = (sync, kind, rawId, deleted = false) => {
    const id = stampKey(rawId);
    if (id === null)
        return;
    sync.seq += 1;
    Vue.set(sync.stamps[kind], id, Object.freeze({ seq: sync.seq, at: performance.now(), deleted }));
};
export const recordApplied = (sync, kind, rawId, requestSeq) => {
    const id = stampKey(rawId);
    if (id === null)
        return;
    Vue.set(sync.stamps[kind], id, Object.freeze({ seq: requestSeq, at: null, deleted: false }));
};
export const recordWriteMany = (sync, kind, rawIds, deleted = false) => {
    const table = { ...sync.stamps[kind] };
    let seq = sync.seq;
    for (const rawId of rawIds) {
        const id = stampKey(rawId);
        if (id === null)
            continue;
        seq += 1;
        table[id] = Object.freeze({ seq, at: performance.now(), deleted });
    }
    sync.seq = seq;
    sync.stamps[kind] = table;
};
export const recordAppliedMany = (sync, kind, rawIds, requestSeq) => {
    const table = { ...sync.stamps[kind] };
    for (const rawId of rawIds) {
        const id = stampKey(rawId);
        if (id === null)
            continue;
        table[id] = Object.freeze({ seq: requestSeq, at: null, deleted: false });
    }
    sync.stamps[kind] = table;
};
export const createSyncRequest = (seq) => Object.freeze({ seq, startedAt: performance.now() });
export const isProtected = (stamp, request) => {
    if (!stamp)
        return false;
    if (stamp.seq > request.seq)
        return true;
    return stamp.at !== null && request.startedAt - stamp.at < STALE_SNAPSHOT_WINDOW_MS;
};
export const isStaleResponse = (sync, request) => request.seq < sync.appliedSeq;
export const collectProtected = (stamps, request) => {
    const result = {};
    Object.keys(stamps).forEach((kind) => {
        result[kind] = {};
        Object.keys(stamps[kind]).forEach((id) => {
            const stamp = stamps[kind][id];
            if (isProtected(stamp, request))
                result[kind][id] = stamp;
        });
    });
    return result;
};
const hasProtected = (protectedByKind) => Object.values(protectedByKind).some((table) => Object.keys(table).length > 0);
const collectPresentIds = (rows, schema, into) => {
    const kind = schema.kind;
    rows.forEach((row) => {
        const id = kind !== null ? stampKey(row[schema.key]) : null;
        if (kind !== null && id !== null) {
            if (!into[kind])
                into[kind] = {};
            into[kind][id] = true;
        }
        Object.entries(schema.children ?? {}).forEach(([childKey, childSchema]) => {
            collectPresentIds(row[childKey] ?? [], childSchema, into);
        });
        if (schema.recursiveChild) {
            collectPresentIds(row[schema.recursiveChild] ?? [], schema, into);
        }
    });
};
const applyOverlays = (base, local, id, schema, protectedByKind) => {
    if (!local || id === null)
        return;
    schema.overlays?.forEach((overlay) => {
        if (!protectedByKind[overlay.kind]?.[id])
            return;
        overlay.fields.forEach((field) => {
            base[field] = local[field];
        });
    });
};
const mergeChildField = (base, local, response, childKey, childSchema, protectedByKind, presentIds) => {
    const responseChildren = response[childKey];
    if (responseChildren === undefined) {
        if (local && local[childKey] !== undefined)
            base[childKey] = local[childKey];
        return;
    }
    const localChildren = local?.[childKey] ?? [];
    base[childKey] = mergeParentArray(localChildren, responseChildren, childSchema, protectedByKind, presentIds);
};
const applyChildren = (base, local, response, schema, protectedByKind, presentIds) => {
    Object.entries(schema.children ?? {}).forEach(([childKey, childSchema]) => {
        mergeChildField(base, local, response, childKey, childSchema, protectedByKind, presentIds);
    });
    if (schema.recursiveChild) {
        mergeChildField(base, local, response, schema.recursiveChild, schema, protectedByKind, presentIds);
    }
};
const mergeNode = (local, response, ownFromLocal, id, schema, protectedByKind, presentIds) => {
    const base = ownFromLocal && local ? { ...local } : { ...response };
    applyOverlays(base, local, id, schema, protectedByKind);
    applyChildren(base, local, response, schema, protectedByKind, presentIds);
    return base;
};
const classifyResponseRow = (response, id, localById, schema, protectedByKind, presentIds) => {
    const kind = schema.kind;
    const stamp = kind !== null && id !== null ? protectedByKind[kind]?.[id] : undefined;
    const local = id !== null ? localById.get(id) : undefined;
    if (stamp?.deleted)
        return null;
    if (stamp && local)
        return mergeNode(local, response, true, id, schema, protectedByKind, presentIds);
    if (stamp && !local) {
        const movedElsewhere = kind !== null && id !== null && Boolean(presentIds[kind]?.[id]);
        return movedElsewhere ? null : mergeNode(null, response, false, id, schema, protectedByKind, presentIds);
    }
    return mergeNode(local ?? null, response, false, id, schema, protectedByKind, presentIds);
};
const keepProtectedLocalOnly = (localRows, respondedIds, schema, protectedByKind) => {
    const kind = schema.kind;
    if (kind === null)
        return [];
    return localRows.filter((row) => {
        const id = stampKey(row[schema.key]);
        if (id === null || respondedIds[id])
            return false;
        const stamp = protectedByKind[kind]?.[id];
        return Boolean(stamp) && !stamp.deleted;
    });
};
const mergeParentArray = (localRows, responseRows, schema, protectedByKind, presentIds) => {
    const localById = new Map();
    localRows.forEach((row) => {
        const id = stampKey(row[schema.key]);
        if (id !== null && !localById.has(id))
            localById.set(id, row);
    });
    const respondedIds = {};
    const merged = [];
    responseRows.forEach((response) => {
        const id = stampKey(response[schema.key]);
        if (id !== null)
            respondedIds[id] = true;
        const row = classifyResponseRow(response, id, localById, schema, protectedByKind, presentIds);
        if (row)
            merged.push(row);
    });
    const keptLocalOnly = keepProtectedLocalOnly(localRows, respondedIds, schema, protectedByKind);
    return schema.insertAt === 'start' ? [...keptLocalOnly, ...merged] : [...merged, ...keptLocalOnly];
};
export const mergeRows = (localRows, responseRows, schema, protectedByKind) => {
    if (!hasProtected(protectedByKind))
        return responseRows;
    const presentIds = {};
    collectPresentIds(localRows, schema, presentIds);
    return mergeParentArray(localRows, responseRows, schema, protectedByKind, presentIds);
};
export const mergeSyncResponse = (sync, localRows, responseRows, schema, request) => {
    if (isStaleResponse(sync, request))
        return localRows;
    const protectedByKind = collectProtected(sync.stamps, request);
    const merged = mergeRows(localRows, responseRows, schema, protectedByKind);
    sync.appliedSeq = request.seq;
    return merged;
};
export const syncMutations = {
    BEGIN_SYNC_REQUEST(state) {
        state.sync.seq += 1;
    },
};
